import type { PropertyCalendarSupply, SupplyConfidence } from '@/api/client'
import { parseCalendarDate } from '@/lib/calendar-date'

/**
 * Presentation rules for the calendar's supplies layer.
 *
 * Every date, quantity, estimate and sentence is the supply model's, returned
 * by admin-api. Nothing here works out a runout or a delivery: the functions
 * pick which of the returned dates get a marker on the grid, name them, and
 * format them.
 */

export type SupplyMarkerKind = 'runout' | 'delivery'

/** One marker on the grid: a runout or a delivery of one category, on its day. */
export interface SupplyMarker {
  /** Stable and unique across categories and kinds, for React. */
  key: string
  category: string
  label: string
  kind: SupplyMarkerKind
  /** How sure the model is of the category's runout: a low-confidence one is drawn as a maybe. */
  confidence: SupplyConfidence
  /** Property-local `YYYY-MM-DD`, as the API returned it. */
  day: string
  /** Deliveries only. */
  qty: number | null
  retailer: string | null
}

/**
 * The markers for the given categories, restricted to the days `inDay` accepts
 * (the visible month: while paging, the month on screen can briefly still be
 * the last one's data).
 *
 * A runout is drawn only when the API says it is `runout_in_range`: a runout
 * past the range is not on the grid, however near it is. Every delivery in the
 * list is drawn, one per delivery date. On a day, runouts come first, then
 * deliveries, each in the API's category order.
 */
export function buildSupplyMarkers(
  supplies: PropertyCalendarSupply[],
  inDay: (day: string) => boolean
): SupplyMarker[] {
  const runouts: SupplyMarker[] = []
  const deliveries: SupplyMarker[] = []
  for (const supply of supplies) {
    const { category, label } = supply
    if (supply.runout_in_range && supply.runout_date && inDay(supply.runout_date)) {
      runouts.push({
        key: `runout:${category}:${supply.runout_date}`,
        category,
        label,
        kind: 'runout',
        confidence: supply.confidence,
        day: supply.runout_date,
        qty: null,
        retailer: null,
      })
    }
    for (const [index, delivery] of supply.deliveries.entries()) {
      if (!inDay(delivery.date)) continue
      deliveries.push({
        key: `delivery:${category}:${delivery.date}:${index}`,
        category,
        label,
        kind: 'delivery',
        confidence: supply.confidence,
        day: delivery.date,
        qty: delivery.qty,
        retailer: delivery.retailer,
      })
    }
  }
  return [...runouts, ...deliveries].sort((a, b) => a.day.localeCompare(b.day))
}

/** Whether the category has a drawn runout or a delivery on `day`: what a marker on that day stands for. */
export function hasSupplyEventOn(supply: PropertyCalendarSupply, day: string): boolean {
  return (
    (supply.runout_in_range && supply.runout_date === day) ||
    supply.deliveries.some(delivery => delivery.date === day)
  )
}

export function groupSupplyMarkersByDay(markers: SupplyMarker[]): Map<string, SupplyMarker[]> {
  const byDay = new Map<string, SupplyMarker[]>()
  for (const marker of markers) {
    const list = byDay.get(marker.day)
    if (list) list.push(marker)
    else byDay.set(marker.day, [marker])
  }
  return byDay
}

/** "Oct 13", or "Oct 13, 2026" with `withYear`; the raw value when it is not a date. */
export function formatSupplyDay(day: string, { withYear = false } = {}): string {
  const date = parseCalendarDate(day)
  if (!date) return day
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  })
}

/** "1 from BJ's Wholesale Club", "2", or "from BJ's" - whatever the delivery says. */
export function describeDelivery(qty: number | null, retailer: string | null): string {
  const parts = [qty === null ? null : String(qty), retailer ? `from ${retailer}` : null]
  return parts.filter(Boolean).join(' ')
}

/**
 * What a supply event adds to its words ("Delivered: batteries"): the day, and
 * for a delivery what arrived ("1 from BJ's Wholesale Club"). The words
 * themselves are the event block's.
 */
export function supplyMarkerDetail(marker: SupplyMarker): string {
  const day = formatSupplyDay(marker.day)
  if (marker.kind === 'runout') return day
  const detail = describeDelivery(marker.qty, marker.retailer)
  return detail ? `${day} (${detail})` : day
}

const OPEN_PREFIX = 'supply:'
const OPEN_PATTERN = /^supply:([^:]+):(\d{4}-\d{2}-\d{2})$/

/** What `?open=` holds for a supply sheet: `supply:<category>:<YYYY-MM-DD>`. */
export function supplyOpenValue(category: string, day: string): string {
  return `${OPEN_PREFIX}${category}:${day}`
}

export function parseSupplyOpen(raw: string | null): { category: string; day: string } | null {
  const match = raw ? OPEN_PATTERN.exec(raw) : null
  return match ? { category: match[1], day: match[2] } : null
}
